<template>
  <div class="app-container">
    <Container
      :title="t('mfa.mfaBind')"
    >
      <template #main>
        <el-row>
          <el-col :span="12">
            <CenterForm
              :formModel="userinfoFormModel"
              :formStruct="userinfoFormStruct"
              :labelWidth="'200px'"
              :readonly="true"
            >
              <template #default="">
                <el-form-item
                  :label="t('mfa.mfaStatus')"
                  class="custom-form-item"
                >
                  <div>
                    {{ mfaStatusText }}
                    <el-button
                      @click="handleBindMFA"
                      class="mfa-btn"
                    >
                      {{ bindMFAText }}
                    </el-button>
                  </div>
                </el-form-item>
              </template>
            </CenterForm>
          </el-col>
        </el-row>
      </template>
    </Container>
    <el-dialog
      center
      v-model="dialogFormVisible"
      width="45%"
    >
      <NormalContainer class="normal-container">
        <div class="mfa-qrCode-img-tip">
          <el-image
            v-if="dialogStatus === 'mfaDialog'"
            class="mfa-qrCode-img"
            :src="qrCodeSrc"
            fit="fill"
          />
          <div
            class="mfa-tip"
            v-if="dialogStatus === 'mfaDialog'"
          >
            {{ t('mfa.mfaBindTip') }}
          </div>
        </div>
        <CenterForm
          v-if="dialogStatus === 'passwordDialog'"
          :formModel="pwFormModel"
          :formStruct="pwFormStruct"
          :formRules="pwFormRole"
          @submit="pwSubmit"
        />
        <CenterForm
          v-if="dialogStatus === 'mfaDialog'"
          :formModel="mfaFormModel"
          :formStruct="mfaFormStruct"
          :formRules="mfaFormRole"
          @submit="mfaSubmit"
        />
      </NormalContainer>
    </el-dialog>
  </div>
</template>

<script lang="ts">
import { defineComponent, onMounted, reactive, toRefs, watch } from 'vue'
import CenterForm from '@/components/form/common/CenterForm.vue'
import { useI18n } from 'vue-i18n'
import { createModel } from '@/utils/component'
import { UserViewModel } from '@/model/userModel'
import Container from '@/components/form/container/Container.vue'
import NormalContainer from '@/components/dfms-components/container/NormalContainer.vue'
import { bindMFA, generateMfaQrCode, getUserInfoById, verifyUser } from '@/apis/user'
import { useStore } from '@/store'
import { UserActionTypes } from '@/store/modules/user/action-types'
import { useRoute, useRouter } from 'vue-router'
import { ElMessage } from 'element-plus'
import { setToken } from '@/utils/cookies'

export default defineComponent({
  components: {
    NormalContainer,
    Container,
    CenterForm
  },
  setup() {
    const { t } = useI18n()
    const store = useStore()
    const route = useRoute()
    const router = useRouter()
    const userinfoStruct = [
      { prop: 'userAccount', label: t('userInfo.userAccount'), component: 'el-input', type: 'text', model: '' }
    ]
    const passwordStruct = [
      { prop: 'password', label: t('mfa.password'), component: 'el-input', type: 'password', model: '' }
    ]
    const passwordRole = {
      password: [{ required: true, message: t('mfa.passwordP'), trigger: 'blur' }]
    }
    const mfaStruct = [
      { prop: 'mfaCode', label: t('mfa.mfaChecksum'), component: 'el-input', type: 'text', model: '', maxlength: 6, showWordLimit: true, clearable: true }
    ]
    const mfaRole = {
      mfaCode: [{ required: true, message: t('mfa.mfaChecksumP'), trigger: 'blur' }]
    }
    const dataMap = reactive({
      userinfoFormStruct: userinfoStruct,
      userinfoFormModel: createModel(userinfoStruct) as UserViewModel,
      pwFormStruct: passwordStruct,
      pwFormModel: createModel(userinfoStruct) as any,
      pwFormRole: passwordRole,
      mfaFormStruct: mfaStruct,
      mfaFormModel: createModel(userinfoStruct) as any,
      mfaFormRole: mfaRole,
      mfaStatus: false,
      mfaStatusText: t('mfa.unbound'),
      bindMFAText: t('mfa.bindNow'),
      dialogStatus: 'passwordDialog',
      dialogFormVisible: false,
      qrCodeSrc: null as string | null,
      temporaryToken: null as string | null,
      async handleBindMFA() {
        dataMap.dialogStatus = 'passwordDialog'
        dataMap.dialogFormVisible = true
      }
    })

    const pwSubmit = async() => {
      const params = {
        userAccount: dataMap.userinfoFormModel.userAccount,
        userPassword: dataMap.pwFormModel.password
      }
      try {
        const data = await verifyUser(params)
        if (data && data.code === 200 && data.data) {
          const token = store.state.user.token
          dataMap.qrCodeSrc = await generateMfaQrCode(token, { width: 300, height: 300 })
          dataMap.dialogStatus = 'mfaDialog'
          dataMap.dialogFormVisible = true
        } else {
          const error = data?.msg ? data.msg : '密码验证失败'
          ElMessage({
            message: error,
            type: 'error',
            duration: 2000
          })
        }
      } catch (e) {
        ElMessage({
          message: e,
          type: 'error',
          duration: 2000
        })
      }
    }
    const mfaSubmit = async() => {
      const token = store.state.user.token
      try {
        const res = await bindMFA(token, { mfaCode: dataMap.mfaFormModel.mfaCode })
        if (res && res.code === 200 && res.data) {
          setToken(res.data)
          dataMap.dialogFormVisible = false
          const msg = t('common.updateSuccess')
          ElMessage({
            message: msg,
            type: 'success',
            duration: 2000
          })
        } else {
          const err = res?.msg ? res.msg : '绑定MFA失败'
          ElMessage({
            message: err,
            type: 'error',
            duration: 2000
          })
        }
      } catch (err) {
        ElMessage({
          message: err,
          type: 'error',
          duration: 2000
        })
      }
    }
    const getUserInfo = async() => {
      if (store.state.user.info) {
        const data = await getUserInfoById(store.state.user.info.userId)
        if (data && data.data) {
          dataMap.mfaStatus = data.data.mfa
          dataMap.userinfoFormModel.userAccount = data.data.userAccount
        }
      } else {
        useStore().dispatch(UserActionTypes.ACTION_LOGIN_OUT, undefined)
        router.push(`/login?redirect=${route.fullPath}`).catch(err => {
          console.warn(err)
        })
      }
    }
    watch(() => dataMap.mfaStatus, (status) => {
      dataMap.mfaStatusText = status ? t('mfa.bonded') : t('mfa.unbound')
      dataMap.bindMFAText = status ? t('mfa.rebind') : t('mfa.bindNow')
    })
    onMounted(() => {
      getUserInfo()
    })
    return {
      t,
      pwSubmit,
      mfaSubmit,
      ...toRefs(dataMap)
    }
  }
})
</script>

<style
  scoped
  lang="scss"
>
.mfa-btn {
  background: none;
  border: none;
  color: #0a76a4;
  margin-left: 20px;
  padding: 0;
  font-size: 12px;
}

.normal-container {
  height: 400px !important;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.mfa-qrCode-img-tip {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.mfa-qrCode-img {
  width: 200px;
  height: 200px;
}

.mfa-tip {
  margin: 20px
}
</style>
